import React, { useEffect, useId, useRef, useState } from 'react';
import { CloseIcon, SearchIcon } from './Icons';
import './MultiReferenceSelect.css';

const MultiReferenceSelect = ({ label, value, options = [], onChange, disabled, required, itemName = 'options', onSearch, loading = false, closeOnAdd = false, compact = false }) => {
  const id = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const activeRef = useRef(null);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const optionLabel = option => `${option.currency_code || option.code} — ${option.name}${option.is_active === false ? ' (Inactive)' : ''}`;
  const available = options.filter(option => !selected.includes(option._id));
  const matches = loading ? [] : onSearch ? available : available.filter(option => optionLabel(option).toLowerCase().includes(search.trim().toLowerCase()));
  const highlighted = Math.min(activeIndex, matches.length - 1);
  const showResults = isOpen && !disabled;

  useEffect(() => {
    if (showResults) activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [showResults, highlighted, search]);

  const add = option => {
    if (!selected.includes(option._id)) onChange([...selected, option._id]);
    setSearch('');
    onSearch?.('');
    setActiveIndex(0);
    if (closeOnAdd) { setIsOpen(false); inputRef.current?.blur(); }
    else inputRef.current?.focus();
  };
  const handleKeyDown = event => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      const next = event.key === 'ArrowDown' ? highlighted + 1 : highlighted - 1;
      setActiveIndex(isOpen ? Math.max(0, Math.min(matches.length - 1, next)) : 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (showResults && matches[highlighted]) add(matches[highlighted]);
    }
  };

  return (
    <fieldset ref={rootRef} className={`reference-multi-select${compact ? ' reference-multi-select-compact' : ''}`} disabled={disabled}
      onBlur={event => { if (!rootRef.current?.contains(event.relatedTarget)) setIsOpen(false); }}>
      <legend id={`${id}-label`}>{label}{required ? ' *' : ''}</legend>
      {!compact && <p id={`${id}-hint`}>Type to search and add {itemName}.</p>}
      <div className={compact ? 'reference-multi-select-control' : undefined}>
        {selected.length > 0 && <div className="reference-multi-select-tags">
          {selected.map(selectedId => {
            const option = options.find(item => item._id === selectedId);
            const text = option ? optionLabel(option) : selectedId;
            return <span className="reference-multi-select-tag" key={selectedId}>
              <span title={compact ? text : undefined}>{compact && option ? option.currency_code || option.code : text}</span>
              <button type="button" className="reference-multi-select-remove" aria-label={`Remove ${text}`}
                onClick={() => onChange(selected.filter(item => item !== selectedId))}><CloseIcon /></button>
            </span>;
          })}
        </div>}
        <div className="reference-multi-select-search">
          {!compact && <span className="reference-multi-select-search-icon" aria-hidden="true"><SearchIcon /></span>}
          <input ref={inputRef} className="search-input reference-multi-select-input" type="text" role="combobox" autoComplete="off"
            aria-labelledby={`${id}-label`} aria-describedby={compact ? undefined : `${id}-hint`} aria-required={required}
            aria-expanded={showResults} aria-controls={showResults ? `${id}-results` : undefined} aria-autocomplete="list"
            aria-activedescendant={showResults && highlighted >= 0 ? `${id}-option-${highlighted}` : undefined}
            placeholder={compact ? (selected.length ? 'Add…' : 'Select…') : `Search ${itemName}...`} value={search} onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)} onKeyDown={handleKeyDown}
            onChange={event => { setSearch(event.target.value); onSearch?.(event.target.value); setActiveIndex(0); setIsOpen(true); }} />
          {showResults && <div className="reference-multi-select-results" id={`${id}-results`} role="listbox" aria-label={label}>
            {matches.map((option, index) => <button key={option._id} ref={index === highlighted ? activeRef : null} type="button" role="option" id={`${id}-option-${index}`}
              aria-selected={index === highlighted} aria-label={`Add ${optionLabel(option)}`}
              className={`reference-multi-select-result${index === highlighted ? ' highlighted' : ''}`}
              onMouseDown={event => event.preventDefault()} onClick={() => add(option)}>
              <span>{optionLabel(option)}</span><span className="reference-multi-select-add">+ Add</span>
            </button>)}
            {!matches.length && <div className="reference-multi-select-empty" role="status">
              {loading ? 'Loading...' : !options.length ? `No ${itemName} available.` : !available.length ? `All ${itemName} added.` : `No matching ${itemName}.`}
            </div>}
          </div>}
        </div>
      </div>
    </fieldset>
  );
};

export default MultiReferenceSelect;
